<script setup>
import { filename } from 'pathe/utils'
import PerfectScrollbar from 'perfect-scrollbar'
import BrancheField from './fields/BrancheField.vue'
import EducationField from './fields/EducationField.vue'
import PLZField from './fields/PLZField.vue'
import PositionField from './fields/PositionField.vue'
import { submitLohnrechner } from '@/lohnrechner'
import { useGAVStore, useLohndatenStore, useResultStore } from '@/stores/lohndaten'
import IntegerSliderField from './fields/IntegerSliderField.vue'
import numeral from 'numeral'
import { inject, ref } from 'vue'

import format from 'string-format'
import { watch } from 'vue'
import { useTextStore } from '@/stores/texts'
import posthog from 'posthog-js'
import { onMounted } from 'vue'
import { computed } from 'vue'

const storeResult = useResultStore()
const store = useLohndatenStore()

const gavStore = useGAVStore()

async function refreshResult() {
  submitLohnrechner(store, storeResult)
}

const glob = import.meta.glob('@/assets/icons/*.svg', { eager: true })
const globInactive = import.meta.glob('@/assets/icons-inactive/*.svg', { eager: true })

const icons = Object.fromEntries(
  Object.entries(glob).map(([key, value]) => [filename(key), value.default])
)
const iconsInactive = Object.fromEntries(
  Object.entries(globInactive).map(([key, value]) => [filename(key), value.default])
)

const showInfoDialog = ref(false)
const step = ref(3)

const t = inject('t')

const MODIFIERS = () => [
  ['branche', t('branche')],
  ['ort', t('ort')],
  ['alter', t('alter')],
  ['erfahrung', t('erfahrung')],
  ['ausbildung', t('ausbildung')],
  ['verantwortung', t('verantwortung')],
  ['pensum', t('pensum')]
]

const selectedModifier = ref(null)

store.$subscribe(() => {
  posthog.capture('form_changed')

  setTimeout(() => {
    refreshResult()
  }, 1)
})

function round(value, to = 10) {
  let divisor = 1 / to
  let rounded = Math.round(value * divisor) * to
  return numeral(rounded).format('0,0').replace(',', '’') + ' CHF'
}

function roundPercentage(value) {
  return numeral(value).format('0.0%').replace('.', ',')
}

function goToStep(stepNumber) {
  if (stepNumber == 1 && !hasMinimumSalary()) return
  step.value = stepNumber
}

defineExpose({
  goToStep
})

const titles = () => [
  '\xa0',
  gavStore.result?.salary.basicSalary ? t('mindestlohn-titel') : t('kein-mindestlohn-titel'),
  t('untere-grenze'),
  t('medianlohn'),
  t('obere-grenze'),
  '\xa0',
]

function has13() {
  return storeResult.request.branche.has_13
}

const miloExplantion = computed(() => {
  if (!gavStore.result) return ''
  if (gavStore.result.salary.has13thMonthSalary) {
    if (pensum.value < 1) {
      return format(t('lohnzusatz-milo-teilzeit'), pensumPercent.value)
    } else {
      return format(t('lohnzusatz-milo'), storeResult.request.branche.weekly_hours)
    }
  } else {
    if (pensum.value < 1) {
      return format(t('lohnzusatz-milo-12-teilzeit'), pensumPercent.value)
    } else {
      return format(t('lohnzusatz-milo-12'), storeResult.request.branche.weekly_hours)
    }
  }
})

const explanations = () => [
  t('zu-tiefer-lohn') + " " + t('zu-tiefer-lohn-unten'),
  miloExplantion.value,
  has13() ? t('lohnzusatz') : t('lohnzusatz-12'),
  has13() ? t('lohnzusatz') : t('lohnzusatz-12'),
  has13() ? t('lohnzusatz') : t('lohnzusatz-12'),
  t('sehr-hoher-lohn')
]

function hasMinimumSalary() {
  return gavStore.result != null
}

watch(
  () => gavStore.result,
  () => {
    if (gavStore.result == null && step.value == 1) {
      step.value = 2
    }
  }
)

const pensum = computed(() => {
  const max_hours = storeResult.request.branche.weekly_hours
  const hours = storeResult.request.stunden
  let pensum = Math.min(hours / max_hours, 1)
  if (pensum > 0.9) pensum = 1
  return pensum
})

const pensumPercent = computed(() => {
  // round to 5%
  let percent = Math.round((pensum.value * 100) / 5) * 5
  return percent + '%'
})

const minimumSalary = computed(() => {
  if (!gavStore.result) return -1
  return gavStore.result.salary.monthSalary * pensum.value
})

const numbers = [
  () => 0,
  () => minimumSalary.value,
  () => storeResult.result.p25,
  () => storeResult.result.p50,
  () => storeResult.result.p75,
  () => storeResult.result.p75
]

function titleForStep(stepNumber) {
  return titles()[stepNumber]
}

const largeNumber = ref(0)
let startTime = null
let animationDuration = 500

function lohnTitle() {
  return format(t('uebliche-loehne'), storeResult.request.beruf.name)
}

function lohnHint() {
  return format(
    t('lohn-hint'),
    storeResult.request.lr_branche,
    storeResult.request.group.name,
    storeResult.request.beruf.name,
    storeResult.request.plz.region.region.name,
    storeResult.request.plz.plz + ' ' + storeResult.request.plz.name,
    numeral(storeResult.request.branche.lohnwachstum).format('0.0'),
  )
}

const scrollPosition = ref('left')

function updateScroller(event) {
  const element = event.target
  if (element.scrollLeft >= element.scrollLeftMax) {
    scrollPosition.value = 'right'
  } else if (element.scrollLeft > 0) {
    scrollPosition.value = 'center'
  } else {
    scrollPosition.value = 'left'
  }
}

onMounted(() => {
  const ps = new PerfectScrollbar('.buttons', {
    suppressScrollY: true,
    suppressScrollX: false,
    swipeEasing: false,
    useBothWheelAxes: true,
    maxScrollbarLength: 320,
    wheelSpeed: 0.5
  })
})

function updateNumber(currentTime) {
  if (largeNumber.value == numbers[step.value]()) return requestAnimationFrame(updateNumber)
  if (!startTime) startTime = currentTime
  let elapsed = currentTime - startTime
  if (elapsed < animationDuration) {
    largeNumber.value =
      largeNumber.value +
      (elapsed / animationDuration) * (numbers[step.value]() - largeNumber.value)
  } else {
    startTime = null
    largeNumber.value = numbers[step.value]()
  }
  requestAnimationFrame(updateNumber)
}

requestAnimationFrame(updateNumber)

function numberForStep(stepNumber) {
  if (stepNumber == 0 || stepNumber == 5) return '\xa0'
  if (stepNumber == 1 && minimumSalary.value == 0) return '\xa0'
  let roundTo = stepNumber == 1 ? 1 : 10
  return round(largeNumber.value, roundTo)
}

function explanationForStep(stepNumber) {
  return explanations()[stepNumber]
}

const languageStore = useTextStore()

languageStore.$subscribe(refreshResult)
</script>

<template>
  <div id="selected" @click="showInfoDialog = !showInfoDialog">
    {{ lohnTitle() }}
    <svg class="showInfoDialog" width="10" height="10" viewBox="0 0 10 10" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M4.5 7.5H5.5V4.5H4.5V7.5ZM5 3.5C5.14167 3.5 5.26042 3.45208 5.35625 3.35625C5.45208 3.26042 5.5 3.14167 5.5 3C5.5 2.85833 5.45208 2.73958 5.35625 2.64375C5.26042 2.54792 5.14167 2.5 5 2.5C4.85833 2.5 4.73958 2.54792 4.64375 2.64375C4.54792 2.73958 4.5 2.85833 4.5 3C4.5 3.14167 4.54792 3.26042 4.64375 3.35625C4.73958 3.45208 4.85833 3.5 5 3.5ZM5 10C4.30833 10 3.65833 9.86875 3.05 9.60625C2.44167 9.34375 1.9125 8.9875 1.4625 8.5375C1.0125 8.0875 0.65625 7.55833 0.39375 6.95C0.13125 6.34167 0 5.69167 0 5C0 4.30833 0.13125 3.65833 0.39375 3.05C0.65625 2.44167 1.0125 1.9125 1.4625 1.4625C1.9125 1.0125 2.44167 0.65625 3.05 0.39375C3.65833 0.13125 4.30833 0 5 0C5.69167 0 6.34167 0.13125 6.95 0.39375C7.55833 0.65625 8.0875 1.0125 8.5375 1.4625C8.9875 1.9125 9.34375 2.44167 9.60625 3.05C9.86875 3.65833 10 4.30833 10 5C10 5.69167 9.86875 6.34167 9.60625 6.95C9.34375 7.55833 8.9875 8.0875 8.5375 8.5375C8.0875 8.9875 7.55833 9.34375 6.95 9.60625C6.34167 9.86875 5.69167 10 5 10ZM5 9C6.11667 9 7.0625 8.6125 7.8375 7.8375C8.6125 7.0625 9 6.11667 9 5C9 3.88333 8.6125 2.9375 7.8375 2.1625C7.0625 1.3875 6.11667 1 5 1C3.88333 1 2.9375 1.3875 2.1625 2.1625C1.3875 2.9375 1 3.88333 1 5C1 6.11667 1.3875 7.0625 2.1625 7.8375C2.9375 8.6125 3.88333 9 5 9Z"
        fill="var(--color)" />
    </svg>
  </div>
  <div class="wrapper" :class="hasMinimumSalary() && minimumSalary > storeResult.result.p25 ? 'special' : 'regular'">
    <div :id="'info-' + step" :class="{ alert: step == 0 || step == 2, neutral: step == 1 }" class="info">
      <div id="info-bulk">
        <h3 id="infoTitle">{{ titleForStep(step) }}</h3>
        <span id="number">
          {{ numberForStep(step) }}
        </span>

        <div id="explain">
          <span v-html="explanationForStep(step)"> </span>
        </div>
      </div>
    </div>
  </div>
  <div id="konisch" :class="hasMinimumSalary() && minimumSalary > storeResult.result.p25 ? 'special' : 'regular'">
    <svg viewBox="-4 -5 106 29.688" version="1.1" xml:space="preserve" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="a" x1="35.446" y1="21.46" x2="4.887" y2="21.46" gradientUnits="userSpaceOnUse">
          <stop stop-color="#FFA4A4" />
          <stop offset="1" stop-color="#FFA4A4" stop-opacity="0" />
        </linearGradient>
        <linearGradient id="e" x1=".23" y1="36.694" x2="320.383" y2="36.694" gradientUnits="userSpaceOnUse">
          <stop stop-color="#FFA4A4" />
          <stop offset=".5" stop-color="#9DFFA1" />
          <stop offset="1" stop-color="#8BDC8F" />
        </linearGradient>
        <linearGradient id="c" x1="35.446" y1="21.46" x2="4.887" y2="21.46" gradientUnits="userSpaceOnUse">
          <stop stop-color="#8bdc8f" offset="0" stop-opacity="1" />
          <stop offset="1" stop-color="#8bdc8f" stop-opacity="0" />
        </linearGradient>
        <filter id="b" x=".887" y=".266" width="38.559" height="42.39" filterUnits="userSpaceOnUse"
          color-interpolation-filters="sRGB">
          <feFlood flood-opacity="0" result="BackgroundImageFix" />
          <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
          <feGaussianBlur stdDeviation="2" result="effect1_foregroundBlur_7435_26076" />
        </filter>
        <filter id="d" x=".887" y=".266" width="38.559" height="42.39" filterUnits="userSpaceOnUse"
          color-interpolation-filters="sRGB">
          <feFlood flood-opacity="0" result="BackgroundImageFix" />
          <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
          <feGaussianBlur stdDeviation="2" result="effect1_foregroundBlur_7435_26076" />
        </filter>
      </defs>
      <path d="M4.887 31.071V12.772l30.559-8.507v34.39z" fill="url(#a)" filter="url(#b)"
        transform="translate(-2.413 9.848) scale(.26458 0.18)" class="areaExtreme" @click="goToStep(0)" />
      <path d="M4.887 31.071V12.772l30.559-8.507v34.39z" fill="url(#c)" filter="url(#d)"
        transform="translate(100.413, 9.848) scale(-0.26458, 0.18)" class="areaExtreme" @click="goToStep(5)" />
      <path
        d="M.23 19.681S100.184.941 158.948.941c58.764 0 107.04 9.594 161.435 18.74v35.507s-102.671 17.26-161.435 17.26C100.184 72.448.23 55.188.23 55.188Z"
        fill="url(#e)" transform="translate(6.586 7.118) scale(.26458 0.18)" />
      <path d="M91.321 19.561V6.82" stroke="#76C97A" stroke-width=".5445056399999999" stroke-linecap="square"
        stroke-dasharray="1.0900695999999999, 1.0900695999999999" fill="none" transform="scale(1 0.8)"
        transform-origin="center" />
      <path d="M6.68 19.561V6.82" stroke="#f47c7c" stroke-width=".5445056399999999" stroke-linecap="square"
        stroke-dasharray="1.0900695999999999, 1.0900695999999999" fill="#12597c" fill-opacity="1" fill-rule="nonzero"
        stroke-opacity="1" transform="scale(1 0.8)" transform-origin="center" />
      <path v-if="hasMinimumSalary()"
        :d="minimumSalary < storeResult.result.p25 ? 'M4.01 19.561V6.82' : 'M25.01 23.561V4.82'" class="minimum-salary"
        stroke="white" :stroke-opacity="0" stroke-width="4" stroke-linecap="square"
        stroke-dasharray="1.0900695999999999, 1.0900695999999999" fill="white" transform="scale(1 0.8)"
        transform-origin="center" @click="goToStep(1)" />

      <path v-if="hasMinimumSalary()"
        :d="minimumSalary < storeResult.result.p25 ? 'M4.01 19.561V6.82' : 'M25.01 23.561V4.82'" class="minimum-salary"
        stroke="white" stroke-width=".5445056399999999" stroke-linecap="square"
        stroke-dasharray="1.0900695999999999, 1.0900695999999999" fill="white" fill-opacity="1" fill-rule="nonzero"
        stroke-opacity="1" transform="scale(1 0.8)" transform-origin="center" @click="goToStep(1)" />
      <path d="M48.986 23.462v-7.023M48.986 10.214V3.19" stroke="#76c97a" stroke-width=".52916" stroke-linecap="square"
        fill="none" transform="scale(1 0.8)" transform-origin="center" />
      <g fill="none" :id="'step-indicator-' + step" class="indicator"
        :class="{ alert: step == 0 || step == 2, neutral: step == 1, info: step > 2 }">
        <circle cx="6.169" cy="7.716" r="9.484" fill="#7ece82" transform="translate(47.368 -3.571) scale(.26458)"
          v-if="step != 2 && step != 4" />
        <circle cx="6.169" cy="7.716" r="9.484" fill="#7ece82" transform="translate(48.368 -3.571) scale(.26458)"
          v-if="step == 2" />
        <circle cx="6.169" cy="7.716" r="9.484" fill="#7ece82" transform="translate(46.368 -3.571) scale(.26458)"
          v-if="step == 4" />
        <path class="line" d="M49 21.32V3.25" stroke="blue" stroke-width=".5445056399999999" stroke-linecap="square"
          stroke-dasharray="1.0900695999999999, 1.0900695999999999" transform="scale(1 0.8) translate(0 0.34)"
          transform-origin="center" />
        <path class="icon" style="transform: translate(47.48px, -3.3px) scale(0.18)"
          d="M9.5711,13.1954v3.15h-2.5v-3.15h2.5ZM9.3494,11.7158h-2.0218l-.2384-4.4961V.7204h2.4651v6.4992l-.205,4.4961Z"
          fill="#34333c" v-if="step == 0" />
        <g v-if="step == 1" style="transform: translate(47.3px, -3.2px) scale(0.09);">
          <path data-v-d1dcf082="" d="M3.83008 18.0859L13.1907 28.0314L31.912 4.04492" fill="#34333C"
            style="stroke-width: 6;stroke: var(--color-background);fill: transparent;">
          </path>
        </g>
        <g v-if="step == 2" style="transform: translate(51.6px, -0.3px) scale(0.16) rotateY(-180deg) rotateZ(-90deg)">
          <path d="M13.5 6L7.50007 12L1.5 6.00001" stroke="#34333C" stroke-width="2.7" />
          <line x1="7.65" y1="11" x2="7.65" y2="-3.83745e-08" stroke="#34333C" stroke-width="2.7" />
          <line x1="15" y1="16.35" x2="-4.29117e-08" y2="16.35" stroke="#34333C" stroke-width="2.7" />
        </g>
        <g v-if="step == 3" style="transform: translate(50.21px, -2.7px) scale(0.16) rotateY(-180deg)">
          <path d="M13.5 6L7.50007 12L1.5 6.00001" stroke="#34333C" stroke-width="2.7" />
          <line x1="7.65" y1="11" x2="7.65" y2="-3.83745e-08" stroke="#34333C" stroke-width="2.7" />
          <line x1="15" y1="16.35" x2="-4.29117e-08" y2="16.35" stroke="#34333C" stroke-width="2.7" />
        </g>
        <g v-if="step == 4" style="transform: translate(46.51px, -2.7px) scale(0.16) rotateY(-180deg) rotateZ(90deg)">
          <path d="M13.5 6L7.50007 12L1.5 6.00001" stroke="#34333C" stroke-width="2.7" />
          <line x1="7.65" y1="11" x2="7.65" y2="-3.83745e-08" stroke="#34333C" stroke-width="2.7" />
          <line x1="15" y1="16.35" x2="-4.29117e-08" y2="16.35" stroke="#34333C" stroke-width="2.7" />
        </g>
        <g v-if="step == 5" style="transform: translate(47.5px, -3.5px) scale(0.2)">
          <rect class="cls-2" x=".8595" y="6.2829" width="5" height="9" fill="var(--color-background)" />
          <polygon class="cls-1"
            points="4.8595 13.2829 9.3595 14.4662 14.6407 14.4662 15.8595 7.56 11.3907 7.56 11.9324 5.3933 11.5262 2.9558 10.3074 2.2787 9.698 5.1902 4.8595 9.2829 4.8595 13.2829"
            stroke="var(--color-background)" />
        </g>
      </g>
      <g id="nr-low">
        <text v-if="step != 2" xml:space="preserve" text-anchor="start" fill="#34333C" x="8" y="15"
          paint-order="markers stroke fill" @click="goToStep(2)">
          <tspan>{{ round(storeResult.result.p25) }}</tspan>
        </text>
      </g>
      <g id="nr-medium">
        <text v-if="step != 3" xml:space="preserve" text-anchor="middle" fill="#34333C"
          paint-order="markers stroke fill" x="50" y="15" @click="goToStep(3)">
          <tspan>{{ round(storeResult.result.p50) }}</tspan>
        </text>
      </g>
      <g id="nr-high">
        <text v-if="step != 4" xml:space="preserve" text-anchor="end" fill="#34333C" paint-order="markers stroke fill"
          x="90" y="15" @click="goToStep(4)">
          <tspan>
            {{ round(storeResult.result.p75) }}
          </tspan>
        </text>
      </g>
    </svg>
  </div>
  <div class="control-wrapper"
    :class="hasMinimumSalary() && minimumSalary > storeResult.result.p25 ? 'special' : 'regular'">
    <div class="control">
      <button @click="goToStep(0)" :class="{ active: step == 0 }" id="button-0">
        <span>1</span>
      </button>
      <button @click="goToStep(2)" :class="{ active: step == 2 }" id="button-3"
        v-if="hasMinimumSalary() && minimumSalary > storeResult.result.p25">
        <span>2</span>
      </button>
      <button @click="goToStep(1)" :class="{ active: step == 1 }" v-if="hasMinimumSalary()" id="button-2">
        <span>2</span>
      </button>
      <button @click="goToStep(2)" :class="{ active: step == 2 }"
        v-if="!hasMinimumSalary() || minimumSalary < storeResult.result.p25" id="button-3">
        <span>2</span>
      </button>
      <button id="button-4" @click="goToStep(3)" :class="{ active: step == 3 }"><span>3</span></button>
      <button id="button-5" @click="goToStep(4)" :class="{ active: step == 4 }"><span>4</span></button>
      <button id="button-6" @click="goToStep(5)" :class="{ active: step == 5 }"><span>5</span></button>
    </div>
  </div>

  <div class="result-hint">
    <p v-if="storeResult.result.is_national">
      {{ t('nationale-berechnung') }}
    </p>
    <p class="source">
      {{ t('source-mark') }}
    </p>
  </div>

  <div class="backdrop" id="info-backdrop" v-if="showInfoDialog" @click="showInfoDialog = false"></div>
  <div class="dialog" id="info-dialog" :class="{ hidden: !showInfoDialog }">
    <svg @click="showInfoDialog = false" width="34" height="34" viewBox="0 0 34 34" fill="none"
      xmlns="http://www.w3.org/2000/svg">
      <g id="Icon">
        <path id="icon"
          d="M26.5038 8.89331L24.5549 6.94434L16.8281 14.6711L9.10132 6.94434L7.15234 8.89331L14.8791 16.6201L7.15234 24.3469L9.10132 26.2958L16.8281 18.5691L24.5549 26.2958L26.5038 24.3469L18.7771 16.6201L26.5038 8.89331Z"
          fill="#34333C" />
      </g>
    </svg>
    <section>
      <p v-html="lohnHint()">
      </p>
    </section>
  </div>

  <Vueform :endpoint="() => { }">
    <div class="backdrop no-print" id="backdrop" v-if="selectedModifier" @click="selectedModifier = null"></div>

    <div class="dialog" id="field-popup" :class="{ hidden: !selectedModifier }">
      <button @click="selectedModifier = null">
        <svg width="34" height="34" viewBox="0 0 34 34" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M26.4999 9.10571L24.551 7.15674L16.8242 14.8835L9.09741 7.15674L7.14844 9.10571L14.8752 16.8325L7.14844 24.5593L9.09741 26.5082L16.8242 18.7815L24.551 26.5082L26.4999 24.5593L18.7732 16.8325L26.4999 9.10571Z"
            fill="#34333C" />
        </svg>
      </button>
      <div v-if="selectedModifier == 'branche'">
        <BrancheField></BrancheField>
      </div>
      <div v-if="selectedModifier == 'ort'">
        <PLZField></PLZField>
      </div>

      <div v-if="selectedModifier == 'alter'">
        <IntegerSliderField :placeholder="t('alter-frage')" :label="t('alter')" name="age" :min="18" :max="64"
          :suffix="t('jahre')">
        </IntegerSliderField>
      </div>
      <div v-if="selectedModifier == 'erfahrung'">
        <IntegerSliderField ref="experienceRef$" :label="t('dienstalter')" name="experience" :suffix="t('jahre')"
          :suffix-singular="t('jahr')" :label-zero="t('weniger-als-ein-jahr')" :min="0" :max="store.age - 18"
          :placeholder="t('berufserfahrung-frage')">
        </IntegerSliderField>
      </div>

      <div v-if="selectedModifier == 'ausbildung'">
        <EducationField></EducationField>
      </div>
      <div v-if="selectedModifier == 'verantwortung'" id="verantwortungPopup">
        <PositionField></PositionField>
      </div>
      <div v-if="selectedModifier == 'pensum'">
        <IntegerSliderField :label="t('pensum')" name="stunden" :suffix="t('stunden')" :placeholder="t('pensum-frage')"
          :min="8" :max="50" :step-size="0.5">
        </IntegerSliderField>
      </div>
    </div>
  </Vueform>
  <div class="buttons-wrapper" :class="{
    left: scrollPosition == 'left',
    center: scrollPosition == 'center',
    right: scrollPosition == 'right'
  }">
    <div class="buttons" @scroll="updateScroller">
      <div v-for="modifier in MODIFIERS()" :key="modifier[0]">
        <button @click="selectedModifier = modifier[0]" :class="{ active: selectedModifier == modifier[0] }">
          <img :src="selectedModifier == modifier[0] ? icons[modifier[0]] : iconsInactive[modifier[0]]" />
        </button>
        <span class="button-label">
          {{ modifier[1] }}
        </span>
      </div>
    </div>
  </div>
  <section id="print-profile" class="printer-only">
    <h3>{{ t('print-profile-title') }}</h3>
    <table>
      <tbody>
        <tr>
          <th>{{ t('beruf') }}</th>
          <td>{{ storeResult.request.beruf.name }}</td>
        </tr>
        <tr>
          <th>{{ t('berufsgruppe') }}</th>
          <td>{{ storeResult.request.group.name }}</td>
        </tr>
        <tr>
          <th>{{ t('branche') }}</th>
          <td>{{ storeResult.request.lr_branche }}</td>
        </tr>
        <tr>
          <th>{{ t('ort') }}</th>
          <td>
            {{ storeResult.request.plz.name }} ({{ t('arbeitsmarktgrossregion') }}:
            {{ storeResult.request.plz.region.region.name }})
          </td>
        </tr>
        <tr>
          <th>{{ t('alter') }}</th>
          <td class="nr">{{ storeResult.request.age }} {{ t('jahre') }}</td>
        </tr>
        <tr>
          <th>{{ t('erfahrung') }}</th>
          <td class="nr">{{ storeResult.request.experience }} {{ t('jahre') }}</td>
        </tr>
        <tr>
          <th>{{ t('ausbildung') }}</th>
          <td>{{ storeResult.request.education.name }}</td>
        </tr>
        <tr>
          <th>{{ t('verantwortung') }}</th>
          <td>{{ storeResult.request.position.name }}</td>
        </tr>
        <tr>
          <th>{{ t('pensum') }}</th>
          <td class="nr">{{ storeResult.request.stunden }} {{ t('stunden') }}</td>
        </tr>
      </tbody>
    </table>
  </section>
  <section class="printer-only" id="printer-info">
    <p v-html="lohnHint()">
    </p>
  </section>
</template>

<style scoped>
#konisch {
  position: relative;
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-content: center;
}

.result-hint {
  font-size: 16px;
  color: white;
  margin-top: 2rem;
  margin-bottom: 2rem;
  min-height: 1px;
}

.button-label {
  text-align: center;
}

.result-hint a {
  color: white;
}

#nr-low {
  transform-origin: center left;
  transition: transform 200ms ease-in-out;
}

#nr-low:hover {
  transform: scale(0.9) translateX(2px);
}

#nr-medium {
  transform-origin: center;
  transition: transform 200ms ease-in-out;
}

#nr-medium:hover {
  transform: scale(0.9);
}

#nr-high {
  transform-origin: center right;
  transition: transform 200ms ease-in-out;
}

#nr-high:hover {
  transform: scale(0.9) translateX(-2px);
}

#konisch,
.buttons,
.control-wrapper,
.wrapper,
.buttons-wrapper,
.result-hint,
#selected {
  animation-name: backInUp;
  animation-duration: 1200ms;
  animation-fill-mode: both;
}

#selected {
  font-weight: normal;
  font-size: 1rem;
}

#konisch svg {
  width: 100%;
}

#konisch .indicator {
  transition: transform 0.5s;
}

#step-indicator-0 {
  transform: translateX(-47px);
}

.regular #step-indicator-1 {
  transform: translateX(-45px);
}

.special #step-indicator-1 {
  transform: translateX(-24px);
}

#step-indicator-2 {
  transform: translateX(-42.3px);
}

#step-indicator-3 {
  transform: translateX(0);
}

#step-indicator-3 .line {
  stroke-dasharray: none;
}

#step-indicator-4 {
  transform: translateX(42.3px);
}

#step-indicator-5 {
  transform: translateX(47.15px);
}
</style>

<style scoped>
.alert .line {
  stroke: #f47c7c;
}

.alert circle {
  fill: #f47c7c;
}

.neutral circle {
  fill: white;
}

.neutral .line {
  stroke: white;
}

.info .line {
  stroke: #76c97a;
}

.info circle {
  fill: #76c97a;
}

div.info {
  width: 100%;
  font-style: normal;
  font-size: 10px;
  font-weight: 300;
  transition: all 0.5s;
  position: absolute;

  display: flex;
  flex-direction: column;
  align-items: center;
}

div.info.alert {
  color: #ffa4a4;
}

#explain {
  color: white;
  --color: white;
}

div.info.neutral {
  color: white;
}

div.info #infoTitle {
  font-weight: 300;
  margin-bottom: 5px;
  font-size: 0.95rem;
  max-width: 13em;
}

div.info #number {
  font-weight: 700;
  font-size: 2.8rem;
}

div.info #explain {
  font-weight: 300;
  font-size: 16px;
  height: 30px;
  display: flex;
  align-items: center;
}

#selected svg {
  color: white;
  margin-left: 0.3rem;
  height: 14px;
  width: 14px;
  transition: all 200ms ease-in-out;
  cursor: pointer;
  --color: white;
}

div.info-popup svg:hover {
  transform: scale(0.9);
}

.wrapper {
  position: relative;
  height: 9rem;
  margin-bottom: 1rem;
  margin-top: 1rem;
  margin-left: -1rem;
  padding-left: 1rem;
  margin-right: -1rem;
  padding-right: 1rem;
  overflow: hidden;
}

#info-bulk {
  display: flex;
  flex-direction: column;
}

#info-0 #info-bulk,
#info-1 #info-bulk,
#info-2 #info-bulk {
  align-items: flex-start;
  transform: translateX(50%);
}

#info-3 #info-bulk {
  color: #9dffa1;
  align-items: center;
}

#info-4 #info-bulk,
#info-5 #info-bulk {
  color: #9dffa1;
  align-items: flex-end;
  transform: translateX(-50%);
}

#info-0 {
  transform: translateX(-51%);
}

.regular #info-1 {
  transform: translateX(-50%);
}

.special #info-1 {
  transform: translateX(-25%);
}

#info-2 {
  transform: translateX(-45%);
}

#info-3 {
  transform: translateX(0);
}

#info-4 {
  transform: translateX(40%);
  text-align: right;
}

#info-5 {
  transform: translateX(45%);
}

text {
  font-size: 3px;
  font-family: Theinhardt;
  font-weight: 700;
  cursor: pointer;
  color: #34333c;
  fill: #34333c;
}

.control {
  display: flex;
  width: 100%;
  position: relative;
  align-items: center;
  justify-items: center;
  justify-content: center;
  border-radius: 13px;
  padding-left: 20px;
  padding-right: 20px;
  height: 25px;

  box-shadow: 0px 4px 4px 0px #34333c40;
}

.control-wrapper {
  display: flex;
  flex-direction: row;
  justify-items: center;
  justify-content: center;
  margin-top: 1rem;
  margin-bottom: 1.5rem;
}

.control button {
  height: 100%;
  border: none;
  background-color: transparent;
  cursor: pointer;
  padding: 0px 10px;
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
}

.control button:hover {
  transform: none;
}

.control button:hover span {
  transform: scale(0.9) translateY(5%);
  transition: all 0.2s;
  background: white;
}

.control button span {
  font-size: 0;
  display: block;
  aspect-ratio: 1;
  border-radius: 100%;
  border: solid white 1px;
  cursor: pointer;
  padding: 0;
  height: 10px;
  width: 10px;
}

.control button.active span {
  background: white;
}

.control button span {
  background: #292735;
}

.buttons {
  display: grid;
  gap: 3px;
  overflow-y: hidden;
  justify-content: space-between;
  grid-auto-flow: column;
  grid-template-columns: repeat(7, minmax(170px, 1fr));
  padding-bottom: 2em;
  padding-top: 1em;
  position: relative;
}

.buttons-wrapper {
  position: relative;
  overflow: hidden;
  margin-bottom: 2em;
}

.buttons div {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5em;
}

.buttons button {
  border-radius: 100%;
  aspect-ratio: 1;
  border: none;
  padding: 10px;
  cursor: pointer;
  width: 50px;
  height: 50px;
  display: flex;
  justify-content: center;
  border: solid 2px var(--color-text);
  box-shadow: rgba(0, 0, 0, 0.25) 0 4px 4px;
  align-items: center;
  background-color: var(--color-text);
  color: white;
}

.buttons button.active {
  background-color: var(--color-background);
  color: var(--color-text);
}

.buttons img {
  height: 30px;
  max-width: 33px;
}

@media screen and (max-width: 600px) {
  #field-popup {
    position: fixed;
    left: 2px;
    right: 2px;
  }
}

.backdrop {
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  z-index: 99;
}

#field-popup {
  position: fixed;
  background: white;
  border-radius: 23px;
  padding-top: 12px;
  padding-bottom: 12px;
  display: flex;
  flex-direction: column;
  padding-right: 23px;
  padding-left: 23px;
  width: calc(min(98vw, var(--frame-width)) - 30px);
  max-width: 597.506px;
  left: 50%;

  top: 100vh;
  margin: auto;
  box-shadow: rgba(0, 0, 0, 0.2) 0px 1px 20px;
  z-index: 1000;
  transform: translate(-50%, calc(min(-100%, -450px) - 10px));
  transition: all 500ms;
  opacity: 1;
  max-height: 88vh;
}

#info-dialog {
  position: fixed;
  background: white;
  left: 50%;
  width: min(90vw, 600px);
  bottom: 100%;
  transform: translate(-50%, calc(100% + 50vh - 50%));
  opacity: 1;
  max-height: 88vh;
  z-index: 9999;
  transition: all 500ms;
  background: #f2ebeb;
  border-radius: 41px;
  box-shadow: 0px 1px 4px 0px rgba(0, 0, 0, 0.25);
  color: #34333c;
  padding: 75px 30px 30px 30px;

  font-size: 19px;
  width: min(78vw, 600px);
  padding: 18px 30px 30px 30px;
  display: flex;
  flex-direction: column;
}

#info-dialog a {
  color: #34333c;
}

#info-dialog p {
  margin: 0;
}

#info-dialog h5 {
  font-weight: 700;
  margin-top: 1rem;
  margin-bottom: 0.2rem;
}

#info-dialog svg {
  align-self: flex-end;
  cursor: pointer;
}

#verantwortungPopup {
  overflow-y: auto;
}

#info-dialog.hidden {
  transform: translate(-50%, 0);
  opacity: 0;
}

#field-popup.hidden {
  transform: translate(-50%, 0);
  opacity: 0;
}

#field-popup>button {
  border: none;
  background: transparent;
  align-self: flex-end;
}

#field-popup>div {
  margin-bottom: 2rem;
}

.buttons-wrapper.left::before {
  content: none;
  opacity: 0;
}

.buttons-wrapper.right::after {
  opacity: 0;
}

.buttons-wrapper::before {
  opacity: 1;
  display: block;
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 4rem;
  background: linear-gradient(90deg, rgba(52, 51, 60, 1) 0%, rgba(52, 51, 60, 0) 100%);
  z-index: 10;
  content: '';
  transition: all 0.2s ease-in-out;
  pointer-events: none;
}

.buttons-wrapper::after {
  opacity: 1;
  display: block;
  position: absolute;
  right: 0;
  top: 0;
  bottom: 0;
  width: 4rem;
  background: linear-gradient(-90deg, rgba(52, 51, 60, 1) 0%, rgba(52, 51, 60, 0) 100%);
  z-index: 10;
  content: '';
  transition: all 0.2s ease-in-out;
  pointer-events: none;
}
</style>

<style>
#field-popup {
  --color-text: #34333c;

  color: var(--color-text);
}

div.info.alert #explain a {
  color: #ffa4a4;
}

form>.vf-errors {
  display: none;
}

.areaExtreme {
  cursor: pointer;
  fill-opacity: 0.6;
  transition: all 200ms ease-in-out;
}

.areaExtreme:hover {
  fill-opacity: 1;
}

div.info.alert #explain {
  color: #ffa4a4;
  --color: #ffa4a4;
}

div.info.neutral #explain {
  --color: white;
  color: white;
}

div.info #explain {
  --color: #9dffa1;
  color: #9dffa1;
}

.buttons .ps__rail-x {
  opacity: 1;
  background-color: #f2ebeb;
  height: 8px;
  color: #f2ebeb;
  border-radius: 10px;
}

path.minimum-salary {
  cursor: pointer;
}

.buttons .ps__thumb-x {
  display: block;
  opacity: 1;
  transition:
    background-color 0.2s linear,
    opacity 0.2s linear;
  -webkit-transition:
    background-color 0.2s linear,
    opacity 0.2s linear;
  height: 8px;
  bottom: 0px;
  position: absolute;
  color: #666778;
  background-color: #666778;
}

#experience .vf-slider-tooltip-top {
  transform: translate(-25%, 0) !important;
}

#print-profile {
  margin-top: 3rem;
}

#print-profile table {
  width: 100%;
  margin-bottom: 1rem;
  border-collapse: collapse;
}

#print-profile th,
#print-profile td {
  border: 2px solid #34333c;
}

#print-profile th {
  text-align: left;
}

#print-profile td.nr {
  text-align: right;
}

#printer-info {
  font-size: 0.8rem;
}

#button-0 {
  left: 3.8%;
}

.regular #button-2 {
  left: 5.5%;
}

.special #button-2 {
  left: 25.5%;
}


#button-3 {
  left: 16%;
}


#button-4 {
  left: 50%;
  transform: translateX(-50%);
}

#button-5 {
  right: 16%;
}

#button-6 {
  right: 3.8%;
}

.source {
  font-style: italic;
  font-size: 0.6rem;
}
</style>
